import { io } from "socket.io-client";
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import styles from './social-message-box.module.css'; // Use CSS modules
import { useAuth } from '../context/AuthContext';
import Notification from './Notification';
import defaultProfile1 from '../images/default-profile1.jpg';
import { useNavigate } from 'react-router-dom';
import { CopyIcon, LeaveGroupIcon } from './icons/Icons';



const getSocketURL = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://server-production-a1b2.up.railway.app/';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const socket = io(getSocketURL(), { 
  transports: ['websocket', 'polling'],
  withCredentials: true,
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000
});



const SocialMessageBox = ({ socialType, social, onClose }) => {
    // console.log("Rendering SocialMessageBox for social:", social._id);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const { userId } = useAuth();
    const [showOptions, setShowOptions] = useState(null);
    // const [showReadBy, setShowReadBy] = useState(null);
    const bottomRef = useRef(null);
    const token = useMemo(() => localStorage.getItem('token'), []);
    const [notification, setNotification] = useState('');
    const [socialChatBoxPosition, setSocialChatBoxPosition] = useState(() => {
        const savedPosition = localStorage.getItem('chatBoxPosition');
        return savedPosition ? JSON.parse(savedPosition) : {  right: 10, bottom: 20 };
    });
    const socialChatBoxRef = useRef(null);
    const [showActiveSocialChatOptions, setShowActiveSocialChatOptions] = useState(false);
    const navigate = useNavigate();

    const handleReceiveMessage = useCallback((newMessage) => {
        // console.log("Received new message via socket:", newMessage.currentSocialId);
        // console.log("Current social ID:", social?._id); 
        if (newMessage.currentSocialId == social?._id) {
            setMessages((prevMessages) => {
                if (prevMessages.some(msg => msg._id === newMessage._id)) {
                    return prevMessages;
                }
                return [...prevMessages, newMessage];
            });
        }
    }, [social?._id]);

    useEffect(() => {
        socket.on('receiveMessage', handleReceiveMessage);
        return () => {
            socket.off('receiveMessage', handleReceiveMessage);
        };
    }, [handleReceiveMessage]);

    useEffect(() => {
        if (!social) return;

        socket.emit('joinRoom', { roomId: social._id });

        return () => {
            socket.emit('leaveRoom', { roomId: social._id });
        };        
    }, [social._id]);


    useEffect(() => {
        const fetchMessages = async () => {
            try {

                const response = await axios.post(`/api/${socialType}Message/get-${socialType}-messages`, { socialId: social._id }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                // const response = await axios.post('/api/groupMessage/get-group-messages', { groupId: group._id }, {
                //     headers: {
                //         Authorization: `Bearer ${token}`
                //     }
                // });
                if (response.data.success) {
                    setMessages(response.data.messages);
                    await axios.post(`/api/${socialType}Message/mark-messages-read`, {
                        socialId: social._id,
                        userId: userId
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    // await axios.post('/api/groupMessage/mark-messages-read', {
                    //     groupId: social._id,
                    //     userId: userId
                    // }, {
                    //     headers: {
                    //         Authorization: `Bearer ${token}`
                    //     }
                    // });
                }
            } catch (error) {
                // console.error('Error fetching social messages:', error.message);
            }
        };

        fetchMessages();
    }, [social._id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        setNewMessage('');                
        try {
            await axios.post(`/api/${socialType}Message/send-${socialType}-message`, {
                socialId: social._id,
                message: newMessage
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
        } catch (error) {
            // console.error('Error sending social message:', error.message);
        }
    };

    const handleDeleteMessage = async (messageId, forEveryone) => {
        
        try {
            await axios.post(`/api/${socialType}Message/delete-${socialType}-message`, {
                messageId,
                forEveryone
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setMessages((prevMessages) => prevMessages.filter(msg => msg._id !== messageId));
            setShowOptions(null)
        } catch (error) {
            // console.error('Error deleting social message:', error);
        }
    };

    const handleCopyMessage = (messageId) => {
        const messageToCopy = messages.find(msg => msg._id === messageId);
        if (messageToCopy) {
            navigator.clipboard.writeText(messageToCopy.message)
                .then(() => {
                    setNotification('Message copied to clipboard!');
                    setTimeout(() => setNotification(''), 3000);
                }
                )
                .catch((err) => {
                    // console.error('Could not copy text: ', err);
                });
        }
    };

    const handleLeaveSocial = async (socialId) => {
        try {
            const response = await axios.post(`/api/${socialType}/leave-${socialType}`, {
                socialId: socialId,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            // const response = await axios.post('/api/group/leave-group', {
            //     groupId,
            // }, {
            //     headers: {
            //         Authorization: `Bearer ${token}`
            //     }
            // });

            if (response.data.success) {
                setNotification(response.data.message || 'Group Left successfully!');
                setTimeout(() => {
                    navigate(-1);
                }, 5000);
            }
        } catch (error) {
            // console.error('Error leaving social:', error);
            const errorMessage = error.response?.data?.message || 'Error leaving social. Please try again.';
            setNotification(errorMessage);
        }
    };
    const handleSocialProfileClick = (socialId) => {
        navigate(`/${socialType}/${socialId}`);
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const now = new Date();
        const isThisYear = d.getFullYear() === now.getFullYear();
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear().toString().slice(-2);
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return isThisYear ? `${hours}:${minutes} ${day}/${month}` : `${hours}:${minutes} ${day}/${month}/${year}`;
    };

    const handleClickOutside = (event) => {
        if (!event.target.closest(`.${styles.messageOptionsButton}`) && !event.target.closest(`.${styles.messageOptionsMenu}`)) {
            setShowOptions(null);
        }
    };
    const handleClickOutside2 = (event) => {
        if (!event.target.closest(`.${styles.closeButton}`) && !event.target.closest(`.${styles.activeGroupChatOptionsMenu}`)) {
            setShowActiveSocialChatOptions(false);
        }   
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);
    useEffect(() => {
        document.addEventListener('click', handleClickOutside2);
        return () => {
            document.removeEventListener('click', handleClickOutside2);
        };
    }, []);

    const handleDragStart = (e) => {
        const socialChatBox = socialChatBoxRef.current;
        const rect = socialChatBox.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        const handleDrag = (e) => {
            const newLeft = e.clientX - offsetX;
            const newTop = e.clientY - offsetY;
            setSocialChatBoxPosition({ top: newTop, left: newLeft });
        };

        const handleDragEnd = () => {
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', handleDragEnd);
            localStorage.setItem('chatBoxPosition', JSON.stringify(socialChatBoxPosition));
        };

        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
    };
    const handleCloseNotification = () => {
        setNotification('');
    };

    return (
        <>
        {notification && (
            <Notification message={notification} onClose={handleCloseNotification} />
        )}
        <div className={styles.groupChatPopup}
            ref={socialChatBoxRef}
            style={{ top: socialChatBoxPosition.top, left: socialChatBoxPosition.left }}>
            <div className={styles.groupChatPopupHeader} onMouseDown={handleDragStart}>
                <div className={styles.groupChatHeaderInfo}>
                    <div className={styles.profileContainer }>
                        <img 
                            title={social.name}
                            src={social.profilePicture ? social.profilePicture.replace('/upload/', '/upload/w_192,h_200,c_fill/') : defaultProfile1}
                            alt={social.name} 
                            className={styles.groupProfilePictureHeader}
                        />
                        <div className={styles.statusDot}></div>
                    </div>
                    <div className={styles.groupChatInfo}>
                        <h3 className={styles.groupChatTitle}>{social.name}</h3>
                        <span className={styles.activeStatus}>status dots</span>
                    </div>
                </div>
                <button title="More Options" onClick={()=>{setShowActiveSocialChatOptions(!showActiveSocialChatOptions)}} className={styles.closeButton}>
                    ⋮
                </button>
                {showActiveSocialChatOptions && (
                    <div className={styles.activeGroupChatOptionsMenu}>
                        <button onClick={() => {handleSocialProfileClick(social._id)}}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="7" r="4"></circle>
                                <path d="M5.5 21a8.38 8.38 0 0 1 13 0"></path>
                            </svg>
                            {socialType.charAt(0).toUpperCase()+ socialType.slice(1)} Profile
                        </button>
                        <button onClick={() => {handleLeaveSocial(social._id)}}>
                            <LeaveGroupIcon />
                            Leave {socialType.charAt(0).toUpperCase()+ socialType.slice(1)}
                        </button>
                    </div>
                )}  
                <button title="Close" onClick={onClose} className={styles.closeButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
            <div className={styles.groupChatBox}>
                <div className={styles.messagesContainer}>
                    {messages.map((msg, index) => (
                        <div key={index} className={`${styles.message} ${msg.senderId._id === userId ? styles.sent : styles.received}`}>
                            <div className={styles.messageContent}>
                                <div className={styles.messageSenderInfo}>
                                    <span className={styles.senderName}>{msg.senderId.name }</span>
                                    <div className={styles.messageText}>{msg.message}</div>
                                </div>
                                <button className={styles.messageOptionsButton} onClick={() => setShowOptions(showOptions === index ? null : index)}>⋮</button>
                                {showOptions === index && ( <>
                                    <div className={styles.messageOptionsMenu}>
                                        <button onClick={() => {handleCopyMessage(msg._id);setShowOptions(null)}}>
                                            <CopyIcon/>
                                            Copy
                                        </button>
                                        <button onClick={() => {handleDeleteMessage(msg._id, false);setShowOptions(null);}}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3,6 5,6 21,6"/>
                                                <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6"/>
                                            </svg>
                                            Delete for me
                                        </button>
                                        {msg.senderId._id === userId && (
                                            <button onClick={() => {handleDeleteMessage(msg._id, true);setShowOptions(null);}}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3,6 5,6 21,6"/>
                                                <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6"/>
                                                </svg>
                                                Delete for everyone
                                            </button>
                                        )}
                                    </div>
                                </>
                                )}
                            </div>
                            <div className={styles.messageTimestamp}>
                                {formatDate(msg.createdAt)}
                            </div>
                        </div>
                    ))}
                    <div ref={bottomRef} />
                </div>
                <div className={styles.inputBox}>
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button onClick={handleSendMessage} className={styles.sendButton} >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default SocialMessageBox;

